'use client';

import { FormattedMessage } from 'react-intl';
import CardLocation from '@alltrails/amplitude/enums/CardLocation';
import logTrailCardClicked from '@alltrails/amplitude/events/logTrailCardClicked';
import logTrailDetailsNearbyTrailClicked from '@alltrails/amplitude/events/logTrailDetailsNearbyTrailClicked';
import TrailStandardCard from '@alltrails/cards/components/TrailStandardCard';
import { PageStrings } from '@alltrails/core/constants/pageStringHelpers';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import useMediaQuery from '@alltrails/core/hooks/useMediaQuery';
import Carousel from '@alltrails/denali/components/Carousel';
import Heading from '@alltrails/denali/components/Heading';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import { BREAKPOINT_MD_SIZE } from '@alltrails/denali/tokens';
import useReduxListItems from '@alltrails/lists/hooks/useReduxListItems';
import type Trail from '@alltrails/types/Trail';
import type { TrailPage } from '../../types/trailPage';
import trailFromAlgoliaTrail from '../../utils/trailFromAlgoliaTrail';
import styles from './TrailsCarousel.module.scss';

const reduxListsAnalyticsInfo = { detailedCardLocation: 'nearby trail card' as CardLocation, page: PageStrings.TRAIL_PAGE };

export type TrailsCarouselProps = {
  mainTrailId: number;
  nearbyTrails: TrailPage['trailPageProps']['nearbyTrails'];
  isBot?: boolean;
};

const TrailsCarousel = ({ isBot = false, mainTrailId, nearbyTrails }: TrailsCarouselProps) => {
  const languageRegionCode = useLanguageRegionCode();
  const isMediumDown = useMediaQuery(`(max-width: ${BREAKPOINT_MD_SIZE}px)`);
  const { handleFavoriteClick, isComplete, isFavorite, isVerified, listModal, signUpModal } = useReduxListItems(
    undefined,
    false /* preventAutoInitialize */,
    reduxListsAnalyticsInfo
  );
  const isMobile = useIsMobileSizedScreen();

  const onTrailClick = (trail: Trail) => {
    logTrailDetailsNearbyTrailClicked({ trail_id: mainTrailId, destination_trail_id: trail.id });
    logTrailCardClicked({
      trail_id: String(trail.id),
      card_location: 'web trail page',
      detailed_card_location: 'web trail page top module'
    });
  };

  const trailsList = isBot ? nearbyTrails : nearbyTrails.slice(0, 12);

  return (
    <section>
      <Heading as="h2" className={styles.sectionTitle} size="400">
        <FormattedMessage defaultMessage="Top trails nearby" />
      </Heading>
      {isBot ? (
        <div className={styles.nearbyTrailsBotContainer}>
          {trailsList.map(trail => {
            const renderableTrail = trailFromAlgoliaTrail(trail);
            return (
              <TrailStandardCard
                key={trail.id}
                isCompleted={isComplete('trail', renderableTrail.id)}
                isFavorite={isFavorite('trail', renderableTrail.id)}
                isPhotoZoomEnabled
                isVerified={isVerified('trail', renderableTrail.id)}
                languageRegionCode={languageRegionCode}
                maxPhotos={1}
                onCardClick={() => onTrailClick(renderableTrail)}
                onFavoriteClick={() => handleFavoriteClick('trail', renderableTrail.id)}
                trail={renderableTrail}
              />
            );
          })}
        </div>
      ) : (
        <Carousel
          className={isMobile ? styles.mobileCarousel : ''}
          getItemKey={trail => `${trail.ID}`}
          items={trailsList}
          renderItem={item => {
            const trail = trailFromAlgoliaTrail(item);
            return (
              <TrailStandardCard
                key={trail.id}
                isCompleted={isComplete('trail', trail.id)}
                isFavorite={isFavorite('trail', trail.id)}
                isPhotoZoomEnabled
                isVerified={isVerified('trail', trail.id)}
                languageRegionCode={languageRegionCode}
                maxPhotos={1}
                onCardClick={() => onTrailClick(trail)}
                onFavoriteClick={() => handleFavoriteClick('trail', trail.id)}
                trail={trail}
              />
            );
          }}
          showOverflow={isMediumDown}
          showPaginationDots={false}
          slidesPerViewBreakpoints={{ 541: 2, 1121: 3 }}
          slidesPerViewMobile={1}
          uniqueId="nearby-trails-carousel"
        />
      )}
      {listModal}
      {signUpModal}
    </section>
  );
};

export default TrailsCarousel;
